import { Link } from "react-router-dom";
import { liveItems } from "@/mocks/lives";
import { formatViewers, getCategoryLabel, sortLives } from "@/utils/live";
import StatusBadge from "@/components/base/StatusBadge";

export default function HeroSection() {
  const featured = sortLives(liveItems, "hot")[0];

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20streaming%20broadcast%20background%2C%20glowing%20azure%20blue%20light%20streaks%20sweeping%20across%20dark%20charcoal%20gradient%2C%20soft%20bokeh%20particles%2C%20cinematic%20depth%2C%20high%20detail%20technology%20mood&width=1920&height=1080&seq=hero-360-live&orientation=landscape"
          alt="360直播 高清视频直播平台"
          title="360直播-高清视频直播平台在线观看"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/60" />
      </div>

      <div className="relative mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-12 lg:gap-12 lg:py-24">
        <div className="lg:col-span-7 animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs font-label font-semibold text-primary-300">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-400 animate-live-pulse" />
            多频道高清直播平台
          </span>
          <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-foreground-950 sm:text-4xl md:text-5xl lg:text-[3.25rem]">
            高清视频直播
            <br />
            一站畅看 <span className="text-primary-400">游戏 · 娱乐 · 体育</span>
          </h1>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            360直播聚合游戏、娱乐、体育等多频道高清直播内容。免费在线观看，实时弹幕互动，快速发现你感兴趣的直播，与千万观众一起享受高品质直播体验。
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/channels"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-lg" />
              立即观看直播
            </Link>
            <Link
              to="/community"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100/70 px-6 py-3 text-sm font-semibold text-foreground-800 backdrop-blur-sm transition-colors hover:border-primary-500/60"
            >
              <i className="ri-chat-smile-2-line text-lg" />
              进入互动社区
            </Link>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
            {[
              { value: "3200+", label: "直播频道" },
              { value: "1200万", label: "日活观众" },
              { value: "1080P", label: "蓝光画质" },
              { value: "7×24", label: "全天候直播" },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col">
                <span className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                  {stat.value}
                </span>
                <span className="text-xs text-foreground-500">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <Link
            to={`/live/${featured.id}`}
            className="group block overflow-hidden rounded-xl border border-background-200/70 bg-background-100/80 backdrop-blur-sm transition-colors hover:border-primary-500/60"
          >
            <div className="relative aspect-video w-full overflow-hidden">
              <img
                src={featured.cover}
                alt={`${featured.streamer} 直播 ${featured.title}`}
                title={`${featured.title} - 360直播`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-transparent to-transparent" />
              <div className="absolute left-3 top-3 flex items-center gap-2">
                <StatusBadge status={featured.status} />
                <span className="rounded-md bg-background-50/70 px-2 py-1 text-[11px] font-medium text-foreground-900 backdrop-blur-sm">
                  焦点推荐
                </span>
              </div>
              <span className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-background-50/70 px-2 py-1 text-[11px] font-medium text-foreground-900 backdrop-blur-sm">
                <i className="ri-eye-line text-xs" />
                {formatViewers(featured.viewers)}
              </span>
            </div>
            <div className="p-5">
              <span className="rounded-full bg-primary-500/15 px-2.5 py-1 text-[11px] font-medium text-primary-300">
                {getCategoryLabel(featured.category)}直播
              </span>
              <h2 className="mt-3 line-clamp-2 font-heading text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-300">
                {featured.title}
              </h2>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500/15 font-label text-xs font-bold text-primary-300">
                    {featured.streamer.slice(0, 1)}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-foreground-800">{featured.streamer}</span>
                    <span className="text-[11px] text-foreground-500">{featured.startTime} 开播</span>
                  </div>
                </div>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform group-hover:scale-110">
                  <i className="ri-play-fill text-lg" />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}